import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil, map } from 'rxjs/operators';

export interface NavigationState {
  isOpen: boolean;
  isCollapsed: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class NavigationService implements OnDestroy {
  private destroy$ = new Subject<void>();
  private readonly COLLAPSED_KEY = 'bdl_nav_collapsed';

  // Single source of truth for navigation state
  private navigationStateSubject = new BehaviorSubject<NavigationState>({
    isOpen: window.innerWidth > 768, // Open on desktop, closed on mobile
    isCollapsed: this.readStoredCollapsed()
  });

  // Public observables
  public readonly navigationState$: Observable<NavigationState> = this.navigationStateSubject.asObservable();
  public readonly isOpen$: Observable<boolean> = this.navigationState$.pipe(
    map(state => state.isOpen),
    takeUntil(this.destroy$)
  );
  public readonly isCollapsed$: Observable<boolean> = this.navigationState$.pipe(
    map(state => state.isCollapsed),
    takeUntil(this.destroy$)
  );

  constructor() {
    this.initializeNavigation();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeNavigation(): void {
    // Listen for window resize events
    window.addEventListener('resize', this.handleResize.bind(this));

    // Auto-close on mobile when service initializes
    if (window.innerWidth <= 768) {
      this.close();
    }
  }

  private handleResize(): void {
    const currentState = this.navigationStateSubject.value;

    if (window.innerWidth <= 768 && currentState.isOpen) {
      // Auto-close on mobile
      this.close();
    }
  }

  // Public methods
  public open(): void {
    this.updateState({ isOpen: true });
  }

  public close(): void {
    this.updateState({ isOpen: false });
  }

  public toggle(): void {
    const currentState = this.navigationStateSubject.value;
    this.updateState({ isOpen: !currentState.isOpen });
  }

  public setCollapsed(collapsed: boolean): void {
    this.updateState({ isCollapsed: collapsed });
  }

  public toggleCollapsed(): void {
    const currentState = this.navigationStateSubject.value;
    this.updateState({ isCollapsed: !currentState.isCollapsed });
  }

  // Getters for current state
  public get isOpen(): boolean {
    return this.navigationStateSubject.value.isOpen;
  }

  public get isCollapsed(): boolean {
    return this.navigationStateSubject.value.isCollapsed;
  }

  public get currentState(): NavigationState {
    return this.navigationStateSubject.value;
  }

  // Auto-close navigation on mobile when component loads
  public autoCloseOnMobile(): void {
    if (window.innerWidth <= 768) {
      this.close();
    }
  }

  // Force close navigation (useful for route changes)
  public forceClose(): void {
    this.updateState({ isOpen: false });
  }

  // Force open navigation
  public forceOpen(): void {
    this.updateState({ isOpen: true });
  }

  private updateState(updates: Partial<NavigationState>): void {
    const currentState = this.navigationStateSubject.value;
    const newState = { ...currentState, ...updates };
    this.navigationStateSubject.next(newState);
    if (updates.isCollapsed !== undefined && updates.isCollapsed !== currentState.isCollapsed) {
      this.storeCollapsed(newState.isCollapsed);
    }
  }

  private readStoredCollapsed(): boolean {
    try {
      return localStorage.getItem(this.COLLAPSED_KEY) === 'true';
    } catch {
      return false;
    }
  }

  private storeCollapsed(collapsed: boolean): void {
    try {
      localStorage.setItem(this.COLLAPSED_KEY, String(collapsed));
    } catch {
      // Storage unavailable (private mode / quota): keep in-memory state only
    }
  }
}